/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { DataGridColumnDataTypes } from '@/shared/types/data-grid-columns';
import { filterFieldsMap as fields } from "./filter-fields-map";
import {
  FilterDefinition,
  FilterOperationMetadata,
} from '@shared/types';
import i18n from '@shared/i18n';

type FilterDefinitionMap = Record<string, FilterDefinition>;

/**
 * Translates a key from the `common` i18n namespace.
 *
 * @param key The translation key to resolve.
 * @returns The translated string for the current locale.
 */
const t = (key: string) => i18n.t(key, { ns: 'common' });

const operations: Record<string, FilterOperationMetadata> = {
  get equals(): FilterOperationMetadata { return { operationType: "equals", label: t('filters.operations.equals') }; },
  get notEquals(): FilterOperationMetadata { return { operationType: "not-equals", label: t('filters.operations.notEquals') }; },
  get range(): FilterOperationMetadata { return { operationType: "range", label: t('filters.operations.range') }; },
  get like(): FilterOperationMetadata { return { operationType: "like", label: t('filters.operations.like') }; },
};

const stringFilter: FilterDefinition = {
  filterDataType: "string",
  operations: [
    { ...operations.equals, fields: [fields.equals] },
    { ...operations.notEquals, fields: [fields.different] },
    { ...operations.like, fields: [fields.like] },
  ],
};

const numberFilter: FilterDefinition = {
  filterDataType: "number",
  operations: [
    { ...operations.equals, fields: [fields.equals] },
    { ...operations.notEquals, fields: [fields.different] },
    { ...operations.range, fields: [fields.greater, fields.lower] },
  ],
};

const booleanFilter: FilterDefinition = {
  filterDataType: "boolean",
  operations: [{ ...operations.equals, fields: [fields.equals] }],
};

const dateFilter: FilterDefinition = {
  filterDataType: "date",
  operations: [
    { ...operations.equals, fields: [fields.equals] },
    { ...operations.notEquals, fields: [fields.different] },
    { ...operations.range, fields: [fields.greater, fields.lower] },
  ],
};

export const filterDefinitions: FilterDefinitionMap = {
  number: numberFilter,
  currency: numberFilter,
  date: dateFilter,
  string: stringFilter,
  boolean: booleanFilter,
};

/**
 * getFilterByDataType function.
 *
 * @param {*} dataType - Parameter description
 *
 * @returns {*} Return value description
 */
export function getFilterByDataType(dataType: DataGridColumnDataTypes) {
  const filter = filterDefinitions[dataType];
  if (!filter)
    throw new Error(`There is no filter for column data type: ${dataType}`);
  return filter;
}
